import styled from 'styled-components';

import EyeIconSvg from '../../../assets/icons/icon-home-dashboard-eye.svg?react';
import LogoMarkSvg from '../../../assets/logos/logo-mark.svg?react';
import { BaseButton } from '../../../shared/ui';

type HomeDashboardBalanceCardProps = {
  brandTagline: string;
  balanceLabel: string;
  balanceValue: string;
  onOpenWithdrawal?: () => void;
  withdrawLabel: string;
};

const BalanceCard = styled.article.attrs({
  'data-ui': 'home-dashboard-balance-card',
})`
  border-radius: ${({ theme }) => theme.scale.px(12)};
  background: #292929;
  padding: ${({ theme }) => theme.scale.px(18)}
    ${({ theme }) => theme.scale.px(14)};
  display: grid;
  gap: ${({ theme }) => theme.scale.px(10)};
`;

const CardHead = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.scale.px(2)};
`;

const LogoMark = styled(LogoMarkSvg).attrs({ 'aria-hidden': true })`
  width: ${({ theme }) => theme.scale.px(97)};
  height: ${({ theme }) => theme.scale.px(30)};
  color: ${({ theme }) => theme.colors.surface.base};
  --logo-mark-accent: ${({ theme }) => theme.colors.brand.primary};
`;

const BrandTagline = styled.p`
  margin: 0;
  font-family: 'Baloo 2', sans-serif;
  font-size: ${({ theme }) => theme.scale.px(8)};
  font-weight: 600;
  line-height: ${({ theme }) => theme.scale.px(12)};
  color: ${({ theme }) => theme.colors.surface.base};
  overflow-wrap: anywhere;
`;

const BalanceBlock = styled.div`
  min-width: 0;
  display: grid;
  gap: ${({ theme }) => theme.scale.px(4)};
`;

const WithdrawPill = styled(BaseButton).attrs({ type: 'button' })`
  margin: 0;
  justify-self: end;
  align-self: end;
  min-width: ${({ theme }) => theme.scale.px(87)};
  min-height: ${({ theme }) => theme.scale.px(30)};
  border-radius: ${({ theme }) => theme.scale.px(8)};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.brand.emphasis};
  font-size: ${({ theme }) => theme.scale.px(12)};
  font-weight: 600;
  line-height: ${({ theme }) => theme.scale.px(14)};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const CardBody = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: end;
  gap: ${({ theme }) => theme.scale.px(8)};

  & > ${WithdrawPill} {
    margin: ${({ theme }) => theme.scale.px(2)}
      ${({ theme }) => theme.scale.px(1)};
  }
`;

const BalanceLabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.scale.px(4)};
`;

const EyeIcon = styled(EyeIconSvg).attrs({ 'aria-hidden': true })`
  width: ${({ theme }) => theme.scale.px(14)};
  height: ${({ theme }) => theme.scale.px(14)};
  color: rgba(255, 255, 255, 0.5);
`;

const BalanceLabel = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(12)};
  line-height: ${({ theme }) => theme.scale.px(14)};
  color: rgba(255, 255, 255, 0.5);
`;

const BalanceValue = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(24)};
  font-weight: 600;
  line-height: ${({ theme }) => theme.scale.px(28)};
  color: ${({ theme }) => theme.colors.surface.base};
`;

export function HomeDashboardBalanceCard({
  brandTagline,
  balanceLabel,
  balanceValue,
  onOpenWithdrawal,
  withdrawLabel,
}: HomeDashboardBalanceCardProps) {
  return (
    <BalanceCard>
      <CardHead>
        <LogoMark />
        <BrandTagline>{brandTagline}</BrandTagline>
      </CardHead>

      <CardBody>
        <BalanceBlock>
          <BalanceLabelRow>
            <BalanceLabel>{balanceLabel}</BalanceLabel>
            <EyeIcon />
          </BalanceLabelRow>
          <BalanceValue>{balanceValue}</BalanceValue>
        </BalanceBlock>
        <WithdrawPill type="button" onClick={onOpenWithdrawal}>
          {withdrawLabel}
        </WithdrawPill>
      </CardBody>
    </BalanceCard>
  );
}
